// Work-pack structure: the component sections a pack is built from, and a
// readiness model over them (what's present, what's still missing).
import type { WorkPackRow, WorkPackTemplate } from "@/api/client";

export interface DocKind {
  value: string;
  label: string;
}

export const DOC_KINDS: DocKind[] = [
  { value: "work_package_plan", label: "Work Package Plan" },
  { value: "task_brief", label: "Task Brief" },
  { value: "job_card", label: "Job Card" },
  { value: "ssow", label: "Safe System of Work / Permit" },
  { value: "risk_assessment", label: "Risk Assessment (RAMS)" },
  { value: "bom", label: "Materials & BOM" },
  { value: "drawing", label: "Drawing / Spec" },
  { value: "other", label: "Other" },
];

export const kindLabel = (k: string): string =>
  DOC_KINDS.find((d) => d.value === k)?.label ?? k;

export interface PackSection {
  key: string;
  label: string;
  blurb: string;
  kinds: string[];
  required: boolean;
}

export const SECTIONS: PackSection[] = [
  {
    key: "plan",
    label: "Plan & brief",
    blurb: "The work package plan, task brief and job cards — what to do.",
    kinds: ["work_package_plan", "task_brief", "job_card"],
    required: true,
  },
  {
    key: "safety",
    label: "Safety & risk",
    blurb: "Safe System of Work / permits and the risk assessment (RAMS).",
    kinds: ["ssow", "risk_assessment"],
    required: true,
  },
  {
    key: "materials",
    label: "Materials & BOM",
    blurb: "Quantities, tools and equipment for the shift.",
    kinds: ["bom"],
    required: false,
  },
  {
    key: "drawings",
    label: "Drawings & specs",
    blurb: "Design drawings and engineering schematics.",
    kinds: ["drawing"],
    required: false,
  },
  {
    key: "supporting",
    label: "Supporting",
    blurb: "Anything else attached to the pack.",
    kinds: ["other"],
    required: false,
  },
];

export function sectionDocs(pack: WorkPackRow, section: PackSection) {
  return (pack.documents ?? []).filter((d) => section.kinds.includes(d.kind));
}

export function sectionForKind(kind: string): PackSection | undefined {
  return SECTIONS.find((s) => s.kinds.includes(kind));
}

// True once the structured Task Brief has meaningful content authored.
export function hasTaskBrief(pack: WorkPackRow): boolean {
  const b = pack.task_brief;
  if (!b) return false;
  return Boolean(
    b.description?.trim() ||
      (b.hazards ?? []).length ||
      (b.ppe ?? []).length ||
      (b.plant ?? []).length ||
      (b.permits ?? []).length,
  );
}

// A document kind counts as "present" when a document of that kind is
// attached — except `bom`, satisfied by a structured BOM line, and
// `task_brief`, satisfied by the structured Task Brief being authored.
function hasKind(pack: WorkPackRow, kind: string): boolean {
  if (kind === "bom" && (pack.bom ?? []).length > 0) return true;
  if (kind === "task_brief" && hasTaskBrief(pack)) return true;
  return (pack.documents ?? []).some((d) => d.kind === kind);
}

export interface ChecklistItem {
  kind: string;
  label: string;
  present: boolean;
}
export interface Readiness {
  mode: "template" | "sections";
  requiredTotal: number;
  requiredPresent: number;
  missing: string[]; // labels of what's still missing
  ready: boolean;
  items: ChecklistItem[]; // the template checklist (empty in sections mode)
}

// When the pack has a task type (and we have its template), readiness is the
// exact required-document checklist; otherwise it falls back to the required
// component sections (Plan & brief, Safety & risk).
export function readiness(
  pack: WorkPackRow,
  templates?: WorkPackTemplate[],
): Readiness {
  const tpl =
    templates && pack.task_type
      ? templates.find((t) => t.task_type === pack.task_type)
      : undefined;
  if (tpl) {
    const items = tpl.items.map((it) => ({ ...it, present: hasKind(pack, it.kind) }));
    const missing = items.filter((i) => !i.present);
    return {
      mode: "template",
      items,
      requiredTotal: items.length,
      requiredPresent: items.length - missing.length,
      missing: missing.map((m) => m.label),
      ready: missing.length === 0,
    };
  }
  const required = SECTIONS.filter((s) => s.required);
  const missing = required.filter((s) => sectionDocs(pack, s).length === 0);
  return {
    mode: "sections",
    items: [],
    requiredTotal: required.length,
    requiredPresent: required.length - missing.length,
    missing: missing.map((s) => s.label),
    ready: missing.length === 0,
  };
}
